import { useId } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

export const MAX_PATTERN_ENTRIES = 50;
export const MAX_PATTERN_LENGTH = 1000;
const EMPTY_PATTERN_PLACEHOLDER = 'To unsubscribe from this group, send an email to…';

interface MessageStripPatternsEditorProps {
	patterns: string[];
	onChange: (next: string[]) => void;
	disabled?: boolean;
	/** When true, hides the built-in "Add pattern" button so the parent can supply its own. */
	hideAddButton?: boolean;
}

export function MessageStripPatternsEditor({ patterns, onChange, disabled, hideAddButton }: MessageStripPatternsEditorProps) {
	const baseId = useId();
	// Show one empty row when the list is empty so the user has something to type into.
	const rows = patterns.length === 0 ? [''] : patterns;
	const atLimit = patterns.length >= MAX_PATTERN_ENTRIES;

	const updateRow = (index: number, value: string) => {
		const next = patterns.length === 0 ? [value] : patterns.map((p, i) => (i === index ? value : p));
		onChange(next);
	};

	const removeRow = (index: number) => {
		if (patterns.length === 0) {
			// The single placeholder row in empty state — clearing it is a no-op.
			onChange([]);
			return;
		}
		onChange(patterns.filter((_, i) => i !== index));
	};

	const addRow = () => {
		if (atLimit) return;
		// Promote the empty-state placeholder row into the list if the user clicks Add before typing.
		const base = patterns.length === 0 ? [''] : patterns;
		onChange([...base, '']);
	};

	return (
		<div className='space-y-3'>
			<div className='space-y-3'>
				{rows.map((value, index) => {
					const overLimit = value.length > MAX_PATTERN_LENGTH;
					const rowId = `${baseId}-row-${index}`;
					return (
						<div key={rowId} className='space-y-1'>
							<div className='flex items-start gap-2'>
								<Textarea
									id={rowId}
									value={value}
									onChange={(e) => updateRow(index, e.target.value)}
									disabled={disabled}
									rows={3}
									placeholder={EMPTY_PATTERN_PLACEHOLDER}
									aria-invalid={overLimit || undefined}
									aria-describedby={`${rowId}-counter`}
									className='min-h-[80px] flex-1 resize-y rounded-xl bg-gray-50 text-xs'
								/>
								<Button
									type='button'
									variant='ghost'
									size='icon'
									onClick={() => removeRow(index)}
									disabled={disabled}
									aria-label={`Remove pattern ${index + 1}`}
									className='mt-1 h-8 w-8 text-muted-foreground hover:text-destructive'
								>
									<Trash2 className='h-3.5 w-3.5' />
								</Button>
							</div>
							<div className='flex items-center justify-between pl-1 pr-10'>
								<span
									id={`${rowId}-counter`}
									className={cn(
										'text-[11px] tabular-nums',
										overLimit ? 'text-destructive font-medium' : 'text-muted-foreground'
									)}
								>
									{value.length}/{MAX_PATTERN_LENGTH}
								</span>
								{overLimit && (
									<span className='text-[11px] font-medium text-destructive'>
										Pattern exceeds {MAX_PATTERN_LENGTH} characters
									</span>
								)}
							</div>
						</div>
					);
				})}
			</div>

			{!hideAddButton && (
				<div className='flex items-center justify-between'>
					<Button
						type='button'
						variant='outline'
						size='sm'
						onClick={addRow}
						disabled={disabled || atLimit}
						className='h-8 gap-1.5'
					>
						<Plus className='h-3.5 w-3.5' />
						Add pattern
					</Button>
					<span className='text-[11px] text-muted-foreground tabular-nums'>
						{patterns.length}/{MAX_PATTERN_ENTRIES}
					</span>
				</div>
			)}
		</div>
	);
}
